import * as Sentry from '@sentry/react';
import type { NavigationFromState } from '@vizit/utils';
import type { Router } from '../../';

/**
 * Navigates to a new route with breadcrumb trail metadata.
 *
 * This function stores the breadcrumb trail in location.state, which preserves
 * the full navigation history across multiple levels of navigation.
 *
 * Use this when navigating to pages that need dynamic breadcrumbs (e.g., Library pages
 * that can be accessed from multiple entry points).
 *
 * @param router - The TanStack router instance
 * @param navigateParams - Navigation parameters
 * @param navigateParams.to - Destination route path
 * @param navigateParams.params - URL parameters
 * @param navigateParams.search - Search parameters
 * @param navigateParams.from - Breadcrumb trail from the current page
 * @returns The navigate function promise
 *
 * @example
 * ```tsx
 * // Navigate from Home page to Asset details (single level)
 * navigateWithState(router, {
 *   to: '/library/assets/$id',
 *   params: { id: assetId },
 *   from: { trail: [{ label: 'Home', href: '/' }] }
 * });
 * ```
 *
 * @example
 * ```tsx
 * // Navigate from PDP to Asset (preserving trail)
 * const currentBreadcrumbs = useBreadcrumbs();
 * navigateWithState(router, {
 *   to: '/library/assets/$id',
 *   params: { id },
 *   from: { trail: currentBreadcrumbs.slice(0, -1) } // Exclude current page
 * });
 * ```
 */
function navigateWithState(
  router: Router,
  navigateParams: {
    to: string;
    params?: Record<string, unknown>;
    search?: Record<string, unknown> | ((prev: Record<string, unknown>) => void);
    from?: NavigationFromState;
  },
) {
  Sentry.addBreadcrumb({
    message: 'Navigating with state',
    level: 'info',
    data: { to: navigateParams.to, has_breadcrumb_trail: !!navigateParams.from },
  });

  const { to, params, search, from } = navigateParams;

  return router.navigate({
    to,
    params,
    search,
    state: from ? { from } : undefined,
  });
}

export { navigateWithState };
